import React from 'react'
import 'project/polyfill'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import Strings from 'project/localisation'
import CommunutyGoalsCard from 'components/cards/CommunityGoalsCard'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { businessSupportStrings } from 'common/strings/business-support-strings'
import AddressSection from 'components/sections/RelationshipsSection'
import FullScreenHero from 'components/hero/FullScreenHero'
import OtherCommunityPages from 'components/company/OtherCommunityPages'
import VisionColumns from 'components/company/VisionColumns'
import VisionImages from 'components/company/VisionImages'
import useWindowSize from 'components/util/useWindowSize'
import classNames from 'classnames'
import TalkToUs from 'components/company/TalkToUs'
import OurPartners from 'components/company/OurPartners'
import imageResize from 'project/image-proxy-web'

export type HomePageType = {}
const HomePage: NextPageWithLayout<HomePageType> = () => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description: businessSupportStrings.seoDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/business-support/',
    },
    title: businessSupportStrings.seoTitle,
  }
  const breadcrumb = homeBreadcrumb().concat([
    {
      item: 'https://www.hoxtonmix.com/business-support/',
      name: `${Strings.businessSupport}`,
      position: 2,
    },
  ])
  const size = useWindowSize()
  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <FullScreenHero
        className={'hero--business-support'}
        title={Strings.businessSupport}
        subtitle={`${businessSupportStrings.forYourTeam}`}
        backgroundImage={imageResize(
          require('public/images/hero/business-support-desktop.jpg'),
        )}
        option={2}
      />
      <main className={'overflow-hidden'}>
        <CommunutyGoalsCard
          className={'pt-6'}
          heroTitle={`${Strings.businessSupport}`}
          heroSubtitle={`${businessSupportStrings.forYourTeam}`}
          title={businessSupportStrings.support}
          subtitle={businessSupportStrings.everythingWeDo}
          subtitleClasses={'mb-xxl-20 mb-xl-14 mb-lg-9 mb-sm-6 mb-1'}
          description={businessSupportStrings.theSupportDescription}
          descriptionClasses={'mb-xl-34 mb-22'}
        />
        <div className='bg-gradient-purple-picton-blue4 pt-xxl-52 pt-lg-21 pt-12 pb-xxl-50 pb-lg-21 pb-10'>
          <div className='container'>
            <VisionColumns
              columnsClasses={'col-xxxl-9 col-xxl-6 col-lg-8'}
              category={businessSupportStrings.support}
              title={businessSupportStrings.businessSupportForSustainableGrowth}
              titleClasses={'pe-lg-0 pe-10'}
              text={businessSupportStrings.forSustainableGrowthContent}
              textClasses={'pe-xxxl-60 pe-sm-26 pe-4 text-semibold'}
            />
          </div>
          <div className='container-lg px-lg-4 px-0'>
            <VisionImages
              images={[
                {
                  alt: 'Support',
                  className:
                    'd-block mt-lg-27 mt-5 ms-auto me-0 position-relative',
                  src: require('public/images/company/support.png'),
                  width: size.isLgOrHigher ? '55%' : '85%',
                },
                {
                  alt: 'Support',
                  className: 'd-block mt-lg-n44 mt-n6',
                  src: require('public/images/company/support-2.png'),
                  width: size.isLgOrHigher ? '65%' : '91%',
                },
              ]}
            />
          </div>
          <div className={'container mt-lg-30 mt-12'}>
            <VisionColumns
              columnsClasses={'col-xxxl-9 col-xxl-7 col-lg-8'}
              category={businessSupportStrings.bursaryProgram}
              title={businessSupportStrings.jobCreationAndEconomicGrowth}
              titleClasses={'pe-0'}
              text={businessSupportStrings.jobCreationAndEconomicGrowthContent}
              textClasses={'pe-xxxl-56 pe-sm-26 pe-10'}
            />
            <div className='row mt-lg-33 mt-8'>
              <div className='col-xxxl-9 col-xxl-7 col-lg-9 pe-xxl-0 pe-lg-18'>
                <h2
                  className={classNames(
                    'text-bold',
                    size.isLgOrHigher ? 'fs-fluid-36-80 lh-105' : 'h3',
                  )}
                  dangerouslySetInnerHTML={{
                    __html:
                      businessSupportStrings.weEnjoyGoodRelationshipsWithTheFollowingPeoplAndOrganisations,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <AddressSection />
        <OurPartners />
        <div
          className={classNames(
            size.isLgOrHigher
              ? 'bg-gradient-purple-picton-blue4'
              : 'bg-gradient-purple-picton-blue5',
            'pt-xxxl-78 pt-72 position-relative',
          )}
        >
          <div
            className={classNames(
              'container-lg px-lg-3 px-0 d-flex justify-content-between align-items-lg-center position-absolute start-0 end-0',
              size.isLgOrHigher ? 'top-n8p' : 'top-n5p',
            )}
          >
            <img
              loading='lazy'
              className='ms-sm-30 mt-lg-0 mt-20 d-lg-inline d-block'
              alt={'Work'}
              style={{
                height: 'auto',
                width: size.isLgOrHigher ? '25%' : '175px',
              }}
              src={imageResize(require('public/images/company/work-1.png'))}
            />
            <img
              loading='lazy'
              className={classNames('d-block me-sm-22')}
              alt={'Community'}
              style={{
                height: 'fit-content',
                width: size.isLgOrHigher ? '30%' : '176px',
              }}
              src={imageResize(
                require('public/images/company/community-5.png'),
              )}
            />
          </div>
          <div className='container'>
            <p
              className={'h4 h4--small text-lg-center text-bold mb-lg-3 mb-4'}
              dangerouslySetInnerHTML={{
                __html: businessSupportStrings.helpingStartups,
              }}
            />
            <h2
              className={classNames(
                'text-lg-center text-bold',
                size.isLgOrHigher ? 'fs-fluid-36-80 lh-105' : 'h3',
              )}
            >
              {businessSupportStrings.alwaysInnovating}
            </h2>
          </div>
          <div className='container-lg d-lg-flex justify-content-between mt-lg-30 mt-18 mb-24 px-lg-4 px-0'>
            <VisionImages
              images={[
                {
                  alt: 'Innovating',
                  className: '',
                  height: 'fit-content',
                  src: require('public/images/company/innovating.png'),
                  width: !size.isSmOrLower ? '26.5%' : '250px',
                },
                {
                  alt: 'Innovating',
                  className: 'd-block mt-lg-31 mt-n4 me-lg-n4 ms-auto me-0',
                  src: require('public/images/company/innovating-2.png'),
                  width: !size.isSmOrLower ? '69.5%' : '320px',
                },
              ]}
            />
          </div>
          <img
            loading='lazy'
            src={imageResize(require('public/images/company/workspace-4.png'))}
            alt={'Workspace'}
            className={'img-fluid mt-lg-0 mt-6 transform-scale-sm-1.5'}
          />
          <div className='pb-xxl-50 pb-lg-21 pb-14'>
            <div className='container-lg px-lg-4 px-0 position-relative mt-sm-n14 mt-22'>
              <VisionImages
                images={[
                  {
                    alt: 'Workspace',
                    className: classNames(
                      'img-fluid d-block position-absolute end-0 me-lg-8',
                      !size.isSmOrLower ? 'top-n30p' : 'top-n30p',
                    ),
                    src: require('public/images/company/inspire-and-infuse-2.png'),
                    width: !size.isSmOrLower ? '38%' : '155px',
                  },
                  {
                    alt: 'Workspace',
                    className: 'img-fluid d-block mx-auto',
                    height: 'fit-content',
                    src: require('public/images/company/workspace-5.png'),
                    width: !size.isSmOrLower ? '66%' : '257px',
                  },
                ]}
              />
            </div>
            <TalkToUs />
          </div>
        </div>
        <OtherCommunityPages
          items={[
            {
              slug: 'social-impact',
              title: 'Social Impact',
            },
            {
              slug: 'outreach',
              title: 'Outreach',
            },
          ]}
        />
        <div className={'spacer'} />
      </main>
    </SeoContainer>
  )
}

export default HomePage
